/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared DTOs for Admin System Settings (Task 11.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * 1. General System Configuration
 */
export const GeneralSettingsSchema = z
  .object({
    siteName: z
      .string()
      .min(1, 'Site name cannot be empty')
      .default('Sophia Platform')
      .openapi({ example: 'Sophia Platform', description: 'Brand site name displayed across headers and metadata' }),
    siteDescription: z
      .string()
      .default('Next-Generation Edge-Native AI SaaS Platform powered by Cloudflare Workers')
      .openapi({ example: 'Next-Generation Edge-Native AI SaaS Platform', description: 'Meta description for SEO' }),
    defaultLocale: z
      .string()
      .default('vi')
      .openapi({ example: 'vi', description: 'Default system fallback locale' }),
    allowedLocales: z
      .array(z.string())
      .default(['vi', 'en', 'ja', 'ko', 'zh'])
      .openapi({ example: ['vi', 'en'], description: 'List of supported user interface locales' }),
    maintenanceMode: z
      .boolean()
      .default(false)
      .openapi({ example: false, description: 'When enabled, only administrators can access the platform' }),
    allowRegistrations: z
      .boolean()
      .default(true)
      .openapi({ example: true, description: 'Whether new end-user signups are open' }),
  })
  .openapi('GeneralSettings');

export type GeneralSettings = z.infer<typeof GeneralSettingsSchema>;

/**
 * 2. Mail & Outbound SMTP / SES Configuration
 */
export const MailSettingsSchema = z
  .object({
    driver: z
      .enum(['smtp', 'resend', 'aws_ses', 'mock'])
      .default('smtp')
      .openapi({ example: 'smtp', description: 'Mail delivery transport engine' }),
    fromName: z
      .string()
      .default('Sophia Platform')
      .openapi({ example: 'Sophia Platform', description: 'Sender display name' }),
    fromEmail: z
      .string()
      .email('Invalid sender email format')
      .default('no-reply@sophiaplatform.com')
      .openapi({ example: 'no-reply@sophiaplatform.com', description: 'Sender RFC email address' }),
    smtpHost: z
      .string()
      .default('smtp.sendgrid.net')
      .openapi({ example: 'smtp.sendgrid.net', description: 'SMTP server hostname or IP' }),
    smtpPort: z
      .number()
      .int()
      .default(587)
      .openapi({ example: 587, description: 'SMTP server port' }),
    smtpUser: z
      .string()
      .default('apikey')
      .openapi({ example: 'apikey', description: 'SMTP server username' }),
    smtpPassword: z
      .string()
      .optional()
      .openapi({ example: '••••••••', description: 'SMTP password (masked on GET responses)' }),
    smtpEncryption: z
      .enum(['none', 'tls', 'ssl'])
      .default('tls')
      .openapi({ example: 'tls', description: 'Transport layer security protocol' }),
    resendApiKey: z
      .string()
      .optional()
      .openapi({ example: '••••••••', description: 'Resend API Key if using Resend driver' }),
    awsSesRegion: z
      .string()
      .default('us-east-1')
      .openapi({ example: 'us-east-1', description: 'AWS SES Region (e.g. us-east-1, ap-southeast-1)' }),
    awsSesAccessKeyId: z
      .string()
      .optional()
      .openapi({ example: 'AKIAIOSFODNN7EXAMPLE', description: 'AWS Access Key ID with ses:SendEmail permission' }),
    awsSesSecretAccessKey: z
      .string()
      .optional()
      .openapi({ example: '••••••••', description: 'AWS Secret Access Key (masked on GET responses)' }),
  })
  .openapi('MailSettings');

export type MailSettings = z.infer<typeof MailSettingsSchema>;

/**
 * 3. AI Inference Default Settings
 */
export const AiDefaultSettingsSchema = z
  .object({
    defaultTextModel: z
      .string()
      .default('mod_gpt_4o_mini')
      .openapi({ example: 'mod_gpt_4o_mini', description: 'Default fallback text inference model' }),
    defaultImageModel: z
      .string()
      .default('mod_flux_1_schnell')
      .openapi({ example: 'mod_flux_1_schnell', description: 'Default fallback image generation model' }),
    defaultVoiceModel: z
      .string()
      .default('mod_tts_1')
      .openapi({ example: 'mod_tts_1', description: 'Default fallback text-to-speech model' }),
    defaultTemperature: z
      .number()
      .min(0)
      .max(2)
      .default(0.7)
      .openapi({ example: 0.7, description: 'Default sampling temperature (0.0 to 2.0)' }),
    defaultMaxTokens: z
      .number()
      .int()
      .min(100)
      .max(32000)
      .default(4096)
      .openapi({ example: 4096, description: 'Default maximum completion tokens' }),
  })
  .openapi('AiDefaultSettings');

export type AiDefaultSettings = z.infer<typeof AiDefaultSettingsSchema>;

/**
 * 4. Billing & Economy Configuration
 */
export const BillingSettingsSchema = z
  .object({
    defaultSignupCredits: z
      .number()
      .int()
      .nonnegative()
      .default(100)
      .openapi({ example: 100, description: 'Initial promotional credits credited to new accounts' }),
    currency: z
      .string()
      .default('USD')
      .openapi({ example: 'USD', description: 'Platform billing settlement base currency' }),
  })
  .openapi('BillingSettings');

export type BillingSettings = z.infer<typeof BillingSettingsSchema>;

/**
 * 5. Social Login Authentication Configuration
 */
export const SocialLoginSettingsSchema = z
  .object({
    google: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: true, description: 'Enable Google OAuth2 login' }),
        clientId: z.string().default('').openapi({ example: '123456789-abc.apps.googleusercontent.com', description: 'Google OAuth2 Client ID' }),
        clientSecret: z.string().optional().openapi({ example: '••••••••', description: 'Google OAuth2 Client Secret (masked on GET)' }),
      })
      .default({ enabled: false, clientId: '' }),
    github: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: false, description: 'Enable GitHub OAuth login' }),
        clientId: z.string().default('').openapi({ example: 'gh_client_12345', description: 'GitHub OAuth Client ID' }),
        clientSecret: z.string().optional().openapi({ example: '••••••••', description: 'GitHub OAuth Client Secret (masked on GET)' }),
      })
      .default({ enabled: false, clientId: '' }),
    apple: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: false, description: 'Enable Sign in with Apple' }),
        clientId: z.string().default('').openapi({ example: 'com.sophiaplatform.web', description: 'Apple Service ID (Client ID)' }),
        teamId: z.string().default('').openapi({ example: 'DEF1234567', description: 'Apple Developer Team ID' }),
        keyId: z.string().default('').openapi({ example: 'KEY1234567', description: 'Apple Key ID' }),
        privateKey: z.string().optional().openapi({ example: '••••••••', description: 'Apple Private Key .p8 content (masked on GET)' }),
      })
      .default({ enabled: false, clientId: '', teamId: '', keyId: '' }),
  })
  .openapi('SocialLoginSettings');

export type SocialLoginSettings = z.infer<typeof SocialLoginSettingsSchema>;

/**
 * 6. Payment Gateways Configuration
 */
export const PaymentGatewaysSettingsSchema = z
  .object({
    stripe: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: true, description: 'Enable Stripe gateway' }),
        sandbox: z.boolean().default(true).openapi({ example: true, description: 'Use Stripe test mode' }),
        publishableKey: z.string().default('').openapi({ example: 'pk_test_...', description: 'Stripe Publishable Key' }),
        secretKey: z.string().optional().openapi({ example: '••••••••', description: 'Stripe Secret Key (masked on GET)' }),
        webhookSecret: z.string().optional().openapi({ example: '••••••••', description: 'Stripe Webhook Signing Secret (masked on GET)' }),
      })
      .default({ enabled: false, sandbox: true, publishableKey: '' }),
    paypal: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: false, description: 'Enable PayPal gateway' }),
        sandbox: z.boolean().default(true).openapi({ example: true, description: 'Use PayPal sandbox mode' }),
        clientId: z.string().default('').openapi({ example: 'client_id_...', description: 'PayPal Client ID' }),
        clientSecret: z.string().optional().openapi({ example: '••••••••', description: 'PayPal Client Secret (masked on GET)' }),
        webhookId: z.string().optional().openapi({ example: '••••••••', description: 'PayPal Webhook ID' }),
      })
      .default({ enabled: false, sandbox: true, clientId: '' }),
    paddle: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: false, description: 'Enable Paddle Billing (Merchant of Record / Global Tax)' }),
        sandbox: z.boolean().default(true).openapi({ example: true, description: 'Use Paddle Sandbox environment' }),
        vendorId: z.string().default('').openapi({ example: '123456', description: 'Paddle Seller / Vendor ID' }),
        clientSideToken: z.string().default('').openapi({ example: 'test_...', description: 'Paddle.js Client-Side Token' }),
        apiKey: z.string().optional().openapi({ example: '••••••••', description: 'Paddle API Secret Key (masked on GET)' }),
        webhookSecret: z.string().optional().openapi({ example: '••••••••', description: 'Paddle Webhook Secret Key (masked on GET)' }),
      })
      .default({ enabled: false, sandbox: true, vendorId: '', clientSideToken: '' }),
    bankTransfer: z
      .object({
        enabled: z.boolean().default(false).openapi({ example: false, description: 'Enable Bank Transfer / VietQR payment' }),
        bankName: z.string().default('').openapi({ example: 'Vietcombank', description: 'Bank institution name' }),
        accountNumber: z.string().default('').openapi({ example: '1029384756', description: 'Bank account number' }),
        accountHolder: z.string().default('').openapi({ example: 'FOUR ELEMENTS CORP', description: 'Bank account holder name' }),
        branch: z.string().default('').openapi({ example: 'Hanoi Branch', description: 'Bank branch location' }),
        qrCodeUrl: z.string().default('').openapi({ example: 'https://r2.sophiaplatform.com/qr/vietqr.png', description: 'Pre-rendered QR image URL' }),
        transferInstructions: z
          .string()
          .default('Please include your Order ID or Workspace ID in the transaction note.')
          .openapi({ example: 'Include order ID', description: 'User guide for manual transfer' }),
      })
      .default({
        enabled: false,
        bankName: '',
        accountNumber: '',
        accountHolder: '',
        branch: '',
        qrCodeUrl: '',
        transferInstructions: 'Please include your Order ID or Workspace ID in the transaction note.',
      }),
  })
  .openapi('PaymentGatewaysSettings');

export type PaymentGatewaysSettings = z.infer<typeof PaymentGatewaysSettingsSchema>;

/**
 * Complete System Settings Catalog
 */
export const SystemSettingsSchema = z
  .object({
    general: GeneralSettingsSchema,
    mail: MailSettingsSchema,
    ai: AiDefaultSettingsSchema,
    billing: BillingSettingsSchema,
    social: SocialLoginSettingsSchema.default({
      google: { enabled: false, clientId: '' },
      github: { enabled: false, clientId: '' },
      apple: { enabled: false, clientId: '', teamId: '', keyId: '' },
    }),
    paymentGateways: PaymentGatewaysSettingsSchema.default({
      stripe: { enabled: false, sandbox: true, publishableKey: '' },
      paypal: { enabled: false, sandbox: true, clientId: '' },
      paddle: { enabled: false, sandbox: true, vendorId: '', clientSideToken: '' },
      bankTransfer: {
        enabled: false,
        bankName: '',
        accountNumber: '',
        accountHolder: '',
        branch: '',
        qrCodeUrl: '',
        transferInstructions: 'Please include your Order ID or Workspace ID in the transaction note.',
      },
    }),
    updatedAt: z
      .string()
      .optional()
      .openapi({ example: '2026-10-26T14:00:00Z', description: 'Timestamp of last modification' }),
  })
  .openapi('SystemSettings');

export type SystemSettings = z.infer<typeof SystemSettingsSchema>;

/**
 * Mutation Request Schema (supports granular partial updates across categories)
 */
export const UpdateSystemSettingsSchema = z
  .object({
    general: GeneralSettingsSchema.partial().optional(),
    mail: MailSettingsSchema.partial().optional(),
    ai: AiDefaultSettingsSchema.partial().optional(),
    billing: BillingSettingsSchema.partial().optional(),
    social: SocialLoginSettingsSchema.partial().optional(),
    paymentGateways: PaymentGatewaysSettingsSchema.partial().optional(),
  })
  .openapi('UpdateSystemSettingsInput');

export type UpdateSystemSettingsInput = z.infer<typeof UpdateSystemSettingsSchema>;
